'use client';
import { useEffect, useState, useCallback } from 'react';

// Extend the Document interface for vendor-prefixed fullscreen properties
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozFullScreenElement?: Element | null;
  mozCancelFullScreen?: () => Promise<void>;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => Promise<void>;
}

// Extend the HTMLElement interface for vendor-prefixed fullscreen methods
interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // ✅ Get the current fullscreen element across browsers
  const getFullscreenElement = useCallback(() => {
    if (typeof document === 'undefined') return null;
    return (
      document.fullscreenElement ||
      (document as FullscreenDocument).webkitFullscreenElement ||
      (document as FullscreenDocument).mozFullScreenElement ||
      (document as FullscreenDocument).msFullscreenElement
    );
  }, []);

  // ✅ Update state when fullscreen changes
  const handleChange = useCallback(() => {
    setIsFullscreen(!!getFullscreenElement());
  }, [getFullscreenElement]);

  // ✅ Enter fullscreen for an element
  const enterFullscreen = useCallback((element: HTMLElement) => {
    if (typeof document === 'undefined') return;

    const el = element as FullscreenElement;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }
  }, []);

  // ✅ Exit fullscreen
  const exitFullscreen = useCallback(() => {
    if (typeof document === 'undefined') return;

    const doc = document as FullscreenDocument;
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    }
  }, []);

  // ✅ Toggle fullscreen on an element
  const toggleFullscreen = useCallback(
    (element: HTMLElement) => {
      if (typeof document === 'undefined') return;
      if (getFullscreenElement()) {
        exitFullscreen();
      } else {
        enterFullscreen(element);
      }
    },
    [getFullscreenElement, enterFullscreen, exitFullscreen],
  );

  // ✅ Listen for fullscreenchange events
  useEffect(() => {
    if (typeof document === 'undefined') return;

    setIsFullscreen(!!getFullscreenElement());

    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    document.addEventListener('mozfullscreenchange', handleChange);
    document.addEventListener('MSFullscreenChange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
      document.removeEventListener('mozfullscreenchange', handleChange);
      document.removeEventListener('MSFullscreenChange', handleChange);
    };
  }, [handleChange, getFullscreenElement]);

  return { isFullscreen, enterFullscreen, exitFullscreen, toggleFullscreen };
}
